<template>
  <div class="space-y-4">
    <div class="flex mb-2">
      <div class="w-1/2 pr-2">
        <label class="font-label text-xs text-fg-secondary uppercase block">First Name</label>
        <input
          id="reservation-form-first-name"
          v-model="form.first_name"
          type="text"
          class="w-full border border-solid border-linear-subtle bg-surface-primary rounded p-2 font-sans focus:border-gray-40 focus:outline-none focus:ring-1 focus:ring-gray-30"
        />
      </div>
      <div class="w-1/2 pl-2">
        <label class="font-label text-xs text-fg-secondary uppercase block">Last Name</label>
        <input
          id="reservation-form-last-name"
          v-model="form.last_name"
          type="text"
          class="w-full border border-solid border-linear-subtle bg-surface-primary rounded p-2 font-sans focus:border-gray-40 focus:outline-none focus:ring-1 focus:ring-gray-30"
        />
      </div>
    </div>
    <div class="flex mb-2">
      <div class="w-1/2 pr-2">
        <label class="font-label text-xs text-fg-secondary uppercase block">Phone</label>
        <input
          id="reservation-form-phone"
          v-model="form.phone"
          type="text"
          class="w-full border border-solid border-linear-subtle bg-surface-primary rounded p-2 font-sans focus:border-gray-40 focus:outline-none focus:ring-1 focus:ring-gray-30"
        />
      </div>
    </div>
    <div class="mb-2">
      <label class="font-label text-xs text-fg-secondary uppercase block">Email</label>
      <input
        id="reservation-form-email"
        v-model="form.email"
        type="text"
        class="w-full border border-solid border-linear-subtle bg-surface-primary rounded p-2 font-sans focus:border-gray-40 focus:outline-none focus:ring-1 focus:ring-gray-30"
      />
    </div>
    <div class="flex mb-2">
      <div class="w-1/2 pr-2">
        <label class="font-label text-xs text-fg-secondary uppercase block -mb-1"
          >Seating Time</label
        >
        <ListBox
          :options="seatingTimes"
          :selected="currentSeatingTime"
          blankText="Choose a seating time"
          itemText="timeStr"
          itemValue="id"
          @listBoxChange="form.seating_time = $event.id"
        />
      </div>
      <div class="w-1/2 pl-2">
        <label class="font-label text-xs text-fg-secondary uppercase block -mb-1"
          >Number of Guests</label
        >
        <ListBox
          :options="numberOfGuestsOptions"
          :selected="currentNumberOfGuestsOption"
          blankText="Choose a number of guests"
          itemText="text"
          itemValue="value"
          @listBoxChange="form.number_of_guests = $event.value"
        />
      </div>
    </div>
    <div class="mb-2">
      <label class="font-label text-xs text-fg-secondary uppercase block">Reservation Notes</label>
      <textarea
        id="reservation-form-internal-notes"
        v-model="form.internal_notes"
        rows="4"
        class="w-full border border-solid border-linear-subtle bg-surface-primary rounded p-2 font-sans focus:border-gray-40 focus:outline-none focus:ring-1 focus:ring-gray-30"
      ></textarea>
    </div>
    <div if="error.message" class="mb-2">
      <span class="font-label text-xs text-fg-secondary uppercase block">{{ error.message }}</span>
    </div>
    <div class="mb-2 flex space-x-4">
      <ButtonDefault
        id="reservation-form-discard"
        label="Cancel"
        alt
        @click="$emit('cancel')"
        classes="w-1/2"
      />
      <ButtonDefault
        id="reservation-form-save"
        label="Save"
        :disabled="formUnsubmittable"
        @click="saveReservation"
        classes="w-1/2"
      />
    </div>
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import { mapState, mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ListBox from '@/common/components/molecules/Listbox.vue'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import { useSettingStore } from '@/stores/setting'
import { ErrorData } from '@/utils/errors'
import { defaultNumberOfGuests, sortBy, sum } from '@/utils/utils'

import { ReservationCreate } from '@generated/types'

interface NumberOfGuestOption {
  value: number
  text: string
  id: number
}

export default defineComponent({
  name: 'ReservationForm',
  components: {
    ButtonDefault,
    ListBox,
  },
  emits: {
    reservationSubmitted(form: ReservationCreate) {
      return !!form
    },
    cancel() {
      return true
    },
  },
  props: {
    error: {
      type: Object as PropType<ErrorData>,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        number_of_guests: 0,
        internal_notes: '',
        seating_time: 0,
      },
    }
  },
  mounted() {
    this.form.number_of_guests = defaultNumberOfGuests(this.selectedTables)
  },
  watch: {
    selectedPerformance: {
      handler(newVal) {
        if (newVal.seating_times.length) {
          this.form.seating_time = newVal.seating_times[0].id
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapStores(useSettingStore),
    ...mapState(usePerformanceReservationStore, ['selectedPerformance', 'selectedTables']),
    formUnsubmittable() {
      return (
        !this.form.number_of_guests ||
        !this.form.first_name ||
        this.form.seating_time < 1 ||
        !this.form.last_name ||
        !this.selectedTables.length
      )
    },
    seatingTimes() {
      if (!this.selectedPerformance!) {
        return []
      }
      return sortBy(
        this.selectedPerformance!.seating_times.map(st => ({
          ...st,
          timeStr: this.settingStore.timeStr(st.time),
        })),
        'time',
      )
    },
    currentSeatingTime() {
      for (const seatingTime of this.seatingTimes) {
        if (this.form.seating_time == seatingTime.id) {
          return seatingTime
        }
      }

      return null
    },
    numberOfGuestsOptions(): NumberOfGuestOption[] {
      const totalOfAvailableGuests = sum(this.selectedTables.map(table => table.available_guests))
      const minimumGuestsAllowed = sum(this.selectedTables.map(table => table.minimum_guests))

      const options: NumberOfGuestOption[] = []
      for (let i = minimumGuestsAllowed; i <= totalOfAvailableGuests; i++) {
        options.push(this.getNumberOfGuestOption(i))
      }
      return options
    },
    currentNumberOfGuestsOption(): NumberOfGuestOption {
      return this.getNumberOfGuestOption(this.form.number_of_guests)
    },
  },
  methods: {
    saveReservation() {
      if (this.formUnsubmittable) {
        return
      }

      this.$emit('reservationSubmitted', {
        ...this.form,
        email:
          this.form.email ||
          `anonymous+res${this.settingStore.venue_id}+${moment().format('x')}@turntabletickets.com`,
        phone: this.form.phone || '555-555-5555',
        performance: this.selectedPerformance!.id,
        tables: this.selectedTables.map(table => table.id),
      })
    },
    getNumberOfGuestOption(value: number): NumberOfGuestOption {
      return {
        value,
        text: value.toString(),
        id: value,
      }
    },
  },
})
</script>
